/* rk-lmb.js — JS de la página /products/leather-medium-bag (familia rk-lmb-*).
   Namespace propio de página (regla de sesiones paralelas de CLAUDE.md).
   Todo con guardas customElements.get(): el script se carga desde varias secciones. */

/* ---------- Add to cart (SOP: /cart/add.js; UpCart intercepta el click y abre su drawer) ---------- */
if (!customElements.get('rk-lmb-atc')) {
  class RkLmbAtc extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.btn = this.querySelector('button[type="submit"]');
      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.#add();
      });
      /* el selector de color de la página actualiza el id de TODOS los forms del producto */
      document.addEventListener('rk-lmb:variant', (event) => {
        if (this.dataset.product !== String(event.detail.product)) return;
        const input = this.form?.querySelector('[name="id"]');
        if (input) input.value = event.detail.variant;
      });
    }

    async #add() {
      const id = parseInt(this.form.querySelector('[name="id"]')?.value || '0', 10);
      const quantity = parseInt(this.form.querySelector('[name="quantity"]')?.value || '1', 10) || 1;
      if (!id) return;
      if (this.btn) this.btn.disabled = true;
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id, quantity }] })
        });
        if (!response.ok) throw new Error('HTTP ' + response.status);
        await this.#afterAdd();
      } catch (error) {
        window.location.href = '/cart';
      } finally {
        if (this.btn) this.btn.disabled = false;
      }
    }

    async #afterAdd() {
      try {
        const cart = await (await fetch('/cart.js')).json();
        for (const el of document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"], [data-cart-count]')) {
          el.textContent = cart.item_count;
        }
      } catch (error) { /* UpCart refresca su propio contador */ }
      for (const name of ['cart:refresh', 'upcart:refresh', 'upcart:open']) {
        document.dispatchEvent(new CustomEvent(name, { bubbles: true }));
      }
      setTimeout(() => {
        if (!document.querySelector('[id*="upcart" i], [class*="upcart" i]')) window.location.href = '/cart';
      }, 1200);
    }
  }
  customElements.define('rk-lmb-atc', RkLmbAtc);
}

/* ---------- Galería: track con scroll-snap + tira de thumbs + flechas ---------- */
if (!customElements.get('rk-lmb-gallery')) {
  class RkLmbGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.thumbs = this.querySelector('[data-thumbs]');
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.thumbBtns = [...this.querySelectorAll('[data-thumb]')];
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');

      this.prev?.addEventListener('click', () => this.#step(-1));
      this.next?.addEventListener('click', () => this.#step(1));
      this.thumbBtns.forEach((btn, i) => btn.addEventListener('click', () => this.go(i)));
      this.track?.addEventListener('scroll', () => this.#onScroll(), { passive: true });
      document.addEventListener('rk-lmb:variant', (event) => {
        if (typeof event.detail.media === 'number' && event.detail.media >= 0) this.go(event.detail.media);
      });
      this.#sync(0);
    }

    get index() {
      if (!this.track) return 0;
      const w = this.slides[0]?.offsetWidth || 1;
      return Math.round(this.track.scrollLeft / w);
    }

    #step(delta) { this.go(this.index + delta); }

    go(i) {
      const index = Math.max(0, Math.min(i, this.slides.length - 1));
      const slide = this.slides[index];
      if (!slide || !this.track) return;
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
      this.#sync(index);
    }

    #onScroll() {
      clearTimeout(this._t);
      this._t = setTimeout(() => this.#sync(this.index), 90);
    }

    #sync(index) {
      this.thumbBtns.forEach((btn, i) => btn.classList.toggle('is-active', i === index));
      /* la tira sigue al slide activo, de a UNA miniatura (aprendizaje acrylic-lamp-kit) */
      const thumb = this.thumbBtns[index];
      if (thumb && this.thumbs) {
        const left = thumb.offsetLeft - this.thumbs.offsetLeft;
        const right = left + thumb.offsetWidth;
        if (left < this.thumbs.scrollLeft) this.thumbs.scrollTo({ left, behavior: 'smooth' });
        else if (right > this.thumbs.scrollLeft + this.thumbs.clientWidth) {
          this.thumbs.scrollTo({ left: right - this.thumbs.clientWidth, behavior: 'smooth' });
        }
      }
      if (this.prev) this.prev.hidden = index === 0;
      if (this.next) this.next.hidden = index === this.slides.length - 1;
    }
  }
  customElements.define('rk-lmb-gallery', RkLmbGallery);
}

/* ---------- Swatches de color: cambian la variante de TODOS los forms + la galería ---------- */
if (!customElements.get('rk-lmb-swatches')) {
  class RkLmbSwatches extends HTMLElement {
    connectedCallback() {
      this.buttons = [...this.querySelectorAll('[data-variant]')];
      this.buttons.forEach((btn) => btn.addEventListener('click', () => this.#select(btn)));
    }

    #select(btn) {
      this.buttons.forEach((b) => {
        const on = b === btn;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      document.dispatchEvent(new CustomEvent('rk-lmb:variant', {
        detail: {
          product: this.dataset.product,
          variant: btn.dataset.variant,
          media: parseInt(btn.dataset.media || '-1', 10)
        }
      }));
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', btn.dataset.variant);
        window.history.replaceState({}, '', url);
      } catch (error) { /* webviews con history bloqueado */ }
    }
  }
  customElements.define('rk-lmb-swatches', RkLmbSwatches);
}

/* ---------- Acordeón: el contenido SIEMPRE en el DOM (max-height, no display:none) ---------- */
if (!customElements.get('rk-lmb-accordion')) {
  class RkLmbAccordion extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('[data-acc-item]')];
      this.single = this.dataset.single === 'true';
      this.items.forEach((item) => {
        const head = item.querySelector('[data-acc-head]');
        head?.addEventListener('click', () => this.#toggle(item));
        head?.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); this.#toggle(item); }
        });
      });
    }

    #toggle(item) {
      const open = item.classList.contains('is-open');
      if (this.single && !open) this.items.forEach((other) => this.#set(other, false));
      this.#set(item, !open);
    }

    #set(item, open) {
      const body = item.querySelector('[data-acc-body]');
      const head = item.querySelector('[data-acc-head]');
      item.classList.toggle('is-open', open);
      head?.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (body) body.style.maxHeight = open ? body.scrollHeight + 'px' : '0px';
    }
  }
  customElements.define('rk-lmb-accordion', RkLmbAccordion);
}

/* ---------- Carrusel simple (community en mobile): flechas + dots ---------- */
if (!customElements.get('rk-lmb-carousel')) {
  class RkLmbCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.items = [...this.querySelectorAll('[data-item]')];
      this.dots = [...this.querySelectorAll('[data-dot]')];
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.#step(-1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.#step(1));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));
      this.track?.addEventListener('scroll', () => {
        clearTimeout(this._t);
        this._t = setTimeout(() => this.#sync(this.index), 90);
      }, { passive: true });
      this.#sync(0);
    }

    get index() {
      const w = this.items[0]?.offsetWidth || 1;
      return Math.round((this.track?.scrollLeft || 0) / w);
    }

    #step(delta) { this.go(this.index + delta); }

    go(i) {
      const index = (i + this.items.length) % this.items.length;
      const item = this.items[index];
      if (!item || !this.track) return;
      this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
      this.#sync(index);
    }

    #sync(index) {
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    }
  }
  customElements.define('rk-lmb-carousel', RkLmbCarousel);
}
